/** Read article/preview fields supporting camelCase and snake_case API responses. */
export const getArticleCoverTitle = (obj) =>
  obj?.coverTitle ?? obj?.cover_title ?? '';

export const getArticleCoverImageUrl = (obj) =>
  obj?.coverImageUrl ?? obj?.cover_image_url ?? '';

/** Cover URL from cover-crop response (supports several BE shapes). */
export const getCoverCropResultImageUrl = (obj) =>
  obj?.coverImageUrl ??
  obj?.cover_image_url ??
  obj?.croppedCoverImageUrl ??
  obj?.cropped_cover_image_url ??
  obj?.croppedImageUrl ??
  obj?.cropped_image_url ??
  obj?.imageUrl ??
  obj?.image_url ??
  obj?.article?.coverImageUrl ??
  obj?.article?.cover_image_url ??
  obj?.cover?.coverImageUrl ??
  obj?.cover?.url ??
  '';

export const getArticleSlideCount = (obj) =>
  obj?.slideCount ?? obj?.slide_count ?? 5;

export const getArticleGenerationPrompt = (obj) =>
  obj?.generationPrompt ?? obj?.generation_prompt ?? '';

export const getArticleGenerationError = (obj) =>
  obj?.generationError ?? obj?.generation_error ?? '';

export const getArticleDiscoverSectionId = (obj) =>
  obj?.discoverSectionId ?? obj?.discover_section_id ?? null;

export const getArticleUpdatedAt = (obj) =>
  obj?.updatedAt ?? obj?.updated_at ?? null;

export const getArticleCoverAspectRatio = (obj) =>
  obj?.coverAspectRatio ?? '5:2';

export const getArticleHotTake = (obj) =>
  obj?.hotTake ?? obj?.hot_take ?? '';

export const getArticleHotTakeOptions = (obj) => {
  const raw = obj?.hotTakeOptions ?? obj?.hot_take_options;
  if (!Array.isArray(raw)) return [];
  return raw.map((o) => String(o ?? '').trim()).filter(Boolean);
};

export const getUploadImageUrl = (data) =>
  data?.imageUrl ?? data?.image_url ?? '';

export const getUploadAspectRatio = (data) =>
  data?.aspectRatio ?? data?.aspect_ratio ?? null;

export const getUploadOrientation = (data) =>
  data?.orientation ?? null;

/** Prefer uncropped hero for cover-crop (avoid re-cropping a finished cover). */
export const getArticleUncroppedHeroUrl = (obj) =>
  obj?.uncroppedHeroUrl ??
  obj?.uncropped_hero_url ??
  obj?.coverSourceImageUrl ??
  obj?.cover_source_image_url ??
  obj?.originalCoverImageUrl ??
  obj?.original_cover_image_url ??
  obj?.sourceImageUrl ??
  obj?.source_image_url ??
  obj?.heroImageUrl ??
  obj?.hero_image_url ??
  obj?.scrapedHeroUrl ??
  obj?.scraped_hero_url ??
  obj?.payload?.uncroppedHeroUrl ??
  obj?.payload?.uncropped_hero_url ??
  obj?.payload?.sourceImageUrl ??
  obj?.payload?.source_image_url ??
  obj?.payload?.cover?.sourceImageUrl ??
  obj?.payload?.cover?.source_image_url ??
  obj?.payload?.cover?.uncroppedHeroUrl ??
  obj?.scrapeResult?.heroImageUrl ??
  obj?.scrape_result?.hero_image_url ??
  '';

/** First usable hero from scrapeRaw[].images[] (prefer wide variants). */
export const getArticleScrapeHeroUrl = (obj) => {
  const raw = obj?.scrapeRaw ?? obj?.scrape_raw;
  if (!Array.isArray(raw)) return '';
  const urls = [];
  for (const item of raw) {
    const images = item?.images;
    if (!Array.isArray(images)) continue;
    for (const img of images) {
      const url = img?.url;
      if (url) urls.push(url);
    }
  }
  if (!urls.length) return '';
  const wide = urls.find((u) => /wide|w_1280|w_1920|original/i.test(u));
  return wide || urls[urls.length - 1] || urls[0];
};

/**
 * Image URL to feed into cover-crop UI.
 * Prefer a true uncropped hero (explicit fields or scrapeRaw); fall back to coverImageUrl.
 */
export const getArticleCropSourceImageUrl = (obj) => {
  const uncropped = getArticleUncroppedHeroUrl(obj);
  if (uncropped) return uncropped;
  const scraped = getArticleScrapeHeroUrl(obj);
  if (scraped) return scraped;
  return getArticleCoverImageUrl(obj) || '';
};

/** Append cache-buster so <img> reloads when CDN filename is new or reused. */
export const withImageCacheBust = (url, token) => {
  if (!url) return '';
  if (token == null || token === '') return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${encodeURIComponent(String(token))}`;
};

/** Prefer flat briefs from article; fall back to preview briefs / payload.briefs. */
export const getArticleBriefsFlat = (article, preview) => {
  if (Array.isArray(article?.briefsFlat) && article.briefsFlat.length) return article.briefsFlat;
  if (Array.isArray(article?.briefs_flat) && article.briefs_flat.length) return article.briefs_flat;
  if (Array.isArray(preview?.briefs) && preview.briefs.length) return preview.briefs;
  if (Array.isArray(article?.payload?.briefs) && article.payload.briefs.length) {
    return article.payload.briefs.map((brief) => {
      const imageBlock = Array.isArray(brief?.blocks)
        ? brief.blocks.find((b) => b?.type === 'image')
        : null;
      const textBlock = Array.isArray(brief?.blocks)
        ? brief.blocks.find((b) => b?.type === 'text')
        : null;
      return {
        id: brief?.id,
        title: brief?.title || '',
        box_text: textBlock?.body ?? brief?.box_text ?? '',
        image_url: imageBlock?.url ?? brief?.image_url ?? '',
        aspect_ratio: imageBlock?.aspectRatio ?? imageBlock?.aspect_ratio ?? brief?.aspect_ratio,
      };
    });
  }
  return [];
};

const getAspectFromBlocks = (blocks) => {
  if (!Array.isArray(blocks)) return null;
  for (const block of blocks) {
    const ratio = block?.aspectRatio ?? block?.aspect_ratio;
    if (ratio) return ratio;
  }
  return null;
};

export const getBriefAspectRatio = (brief) =>
  brief?.aspect_ratio ??
  brief?.aspectRatio ??
  getAspectFromBlocks(brief?.blocks) ??
  getAspectFromBlocks(brief?.payload?.blocks) ??
  null;

export const getBriefImageUrl = (brief) =>
  brief?.image_url ?? brief?.imageUrl ?? '';

export const getBriefBoxText = (brief) =>
  brief?.box_text ?? brief?.boxText ?? '';

/** Pulse news body — 140–150 target, hard max 150, no trailing … */
export const SUMMARY_MAX_CHARS = 150;

/**
 * Hard max 150 chars (target 140–150); never append trailing ellipsis (Pulse copy rule).
 */
export function clampDeckSummary(text, maxLen = SUMMARY_MAX_CHARS) {
  const cap = Math.max(80, Math.min(SUMMARY_MAX_CHARS, Number(maxLen) || SUMMARY_MAX_CHARS));
  let summary = String(text || '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!summary) return '';
  if (summary.length <= cap) return summary;
  return summary.slice(0, cap).replace(/\s+\S*$/, '').trim() || summary.slice(0, cap).trim();
}

/** Pulse news deck body — same string as app / Pulse card. Prefer API fields. */
export const getArticleDeckSummary = (obj, maxLen = SUMMARY_MAX_CHARS) => {
  const cap = Math.max(80, Math.min(SUMMARY_MAX_CHARS, Number(maxLen) || SUMMARY_MAX_CHARS));
  const stored = obj?.summary ?? obj?.deckSummary ?? obj?.deck_summary;
  if (stored != null && String(stored).trim()) {
    return clampDeckSummary(String(stored), cap);
  }
  // Fallback for older rows without summary — synthesize from briefs (no trailing …)
  const briefs = getArticleBriefsFlat(obj, null);
  const parts = [];
  for (const brief of briefs.slice(0, 3)) {
    const text = String(getBriefBoxText(brief) || brief?.title || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) continue;
    if (parts.some((p) => p.toLowerCase() === text.toLowerCase())) continue;
    parts.push(text);
    if (parts.join(' ').length >= cap) break;
  }
  let summary = parts.join(' ').replace(/\s+/g, ' ').trim();
  if (!summary) {
    summary = String(getArticleCoverTitle(obj) || '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  return clampDeckSummary(summary, cap);
};

export const isLandscapeAspect = (ratio) => {
  if (!ratio) return false;
  const normalized = String(ratio).replace(/\s/g, '');
  return normalized === '16:9' || normalized === '16/9';
};

export const getSlideCountForBadge = (article, preview) => {
  const slides = preview?.slides;
  if (Array.isArray(slides) && slides.length) return slides.length;
  const briefs = getArticleBriefsFlat(article, preview);
  return briefs.length;
};
